// pages/ClientApproval.jsx - Public approval page with PDF viewer and optional commenting
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DocumentViewer from '../components/DocumentViewer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Page translations for French and English
const pageTranslations = {
  en: {
    title: 'Document Review',
    subtitle: 'Review the translated document below',
    addComments: 'Add Comments',
    approve: 'Approve Document',
    approving: 'Approving...',
    approved: 'Approved',
    missingDoc: 'Missing Document',
    missingDocMsg: 'No document file was provided. Please check your approval link.',
    goHome: 'Go to Homepage',
    loading: 'Loading approval status...',
    status: 'Status',
    tip: 'You can review this document without logging in. Click "Add Comments in Google Docs" above if you need to leave feedback.',
    successMsg: 'Document approved successfully',
    downloadDocx: 'Download DOCX',
    successModalTitle: 'Document Approved!',
    successModalMessage: 'Thank you for reviewing and approving this document. You can now close this page.',
    closeButton: 'Close',
  },
  fr: {
    title: 'Validation du document',
    subtitle: 'Consultez le document traduit ci-dessous',
    addComments: 'Ajouter des commentaires',
    approve: 'Valider le document',
    approving: 'Validation en cours...',
    approved: 'Validé',
    missingDoc: 'Document manquant',
    missingDocMsg: 'Aucun fichier de document n\'a été fourni. Veuillez vérifier votre lien de validation.',
    goHome: 'Retour à l\'accueil',
    loading: 'Chargement du statut de validation...',
    status: 'Statut',
    tip: 'Vous pouvez consulter ce document sans vous connecter. Cliquez sur "Ajouter des commentaires dans Google Docs" ci-dessus si vous souhaitez laisser un commentaire.',
    successMsg: 'Document validé avec succès',
    downloadDocx: 'Télécharger DOCX',
    successModalTitle: 'Document validé !',
    successModalMessage: 'Merci d\'avoir examiné et validé ce document. Vous pouvez maintenant fermer cette page.',
    closeButton: 'Fermer',
  }
};

const ClientApproval = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('documentId');
  const pdfUrl = searchParams.get('pdfUrl');
  const lang = searchParams.get('lang') || 'fr';
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [isLoadingApproval, setIsLoadingApproval] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Get translations for current language, fallback to French
  const t = pageTranslations[lang] || pageTranslations.fr;

  useEffect(() => {
    if (!token) {
      setIsLoadingApproval(false);
      return;
    }

    const loadApprovalStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/approvals/token/${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || 'Failed to load approval status');
        }

        setApprovalStatus(data.approval);
      } catch (error) {
        setSubmitError(error.message);
      } finally {
        setIsLoadingApproval(false);
      }
    };

    loadApprovalStatus();
  }, [token]);

  // If no PDF URL provided, show error
  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t.missingDoc}</h2>
          <p className="text-gray-600 mb-6">
            {t.missingDocMsg}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.goHome}
          </a>
        </div>
      </div>
    );
  }

  // Construct Google Docs URL for commenting (if documentId available)
  const googleDocsUrl = documentId
    ? `https://docs.google.com/document/d/${documentId}/edit`
    : null;

  // Convert Dropbox URL for embedding (dl=0 to raw=1)
  const embedUrl = pdfUrl.replace('dl=0', 'raw=1');

  const handleDownloadDocx = () => {
    if (!token) return;
    const downloadUrl = `${API_URL}/approvals/token/${token}/docx`;
    window.open(downloadUrl, '_blank');
  };

  const handleApprove = async () => {
    if (!token || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitMessage(null);

    try {
      const response = await fetch(`${API_URL}/approvals/token/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: true }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to submit approval');
      }

      setApprovalStatus((prev) => prev ? { ...prev, status: 'APPROVED', approvedAt: data.approval?.approvedAt } : prev);
      setShowSuccessModal(true);
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isApproved = approvalStatus?.status === 'APPROVED';
  const isPending = approvalStatus?.status === 'PENDING';

  // Get button text based on state
  const getButtonText = () => {
    if (isApproved) return t.approved;
    if (isSubmitting) return t.approving;
    return t.approve;
  };

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 sm:p-8 max-w-md w-full text-center shadow-xl">
            <div className="text-5xl sm:text-6xl mb-4">✅</div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">{t.successModalTitle}</h2>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">{t.successModalMessage}</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full sm:w-auto px-6 py-3 bg-green-300 rounded-lg hover:bg-green-400 transition-colors font-medium"
            >
              {t.closeButton}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-slate-700 text-white p-3 sm:p-4 shadow-md">
        <div className="max-w-7xl mx-auto">
          {/* Mobile: Stack layout, Desktop: Flex row */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
            {/* Title */}
            <div className="text-center sm:text-left">
              <h1 className="text-lg sm:text-xl font-bold">{t.title}</h1>
              <p className="text-xs sm:text-sm text-blue-100 mt-0.5 sm:mt-1">
                {t.subtitle}
              </p>
            </div>

            {/* Action Buttons - Horizontal scroll on mobile */}
            <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3 overflow-x-auto pb-1 sm:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
              {/* Download DOCX Button */}
              <button
                type="button"
                onClick={handleDownloadDocx}
                disabled={!token}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium disabled:opacity-50 text-sm sm:text-base whitespace-nowrap flex-shrink-0"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden xs:inline">{t.downloadDocx}</span>
                <span className="xs:hidden">DOCX</span>
              </button>

              {/* Add Comments Button */}
              {googleDocsUrl && (
                <a
                  href={googleDocsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm sm:text-base whitespace-nowrap flex-shrink-0"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <span className="hidden sm:inline">{t.addComments}</span>
                  <span className="sm:hidden">{lang === 'fr' ? 'Commenter' : 'Comment'}</span>
                </a>
              )}

              {/* Approve Button */}
              <button
                type="button"
                onClick={handleApprove}
                disabled={!isPending || isSubmitting}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base whitespace-nowrap flex-shrink-0 ${
                  isPending
                    ? 'bg-green-300  hover:bg-green-300'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                ✅ <span className="hidden xs:inline">{getButtonText()}</span>
                <span className="xs:hidden">{isApproved ? '✓' : (isSubmitting ? '...' : (lang === 'fr' ? 'Valider' : 'OK'))}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-auto p-2 sm:p-4 md:p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg">
          <DocumentViewer
            documentUrl={embedUrl}
            title={t.title}
          />
        </div>
      </div>

      {/* Approval Status */}
      <div className="bg-white border-t border-gray-200 p-2 sm:p-3">
        <div className="max-w-7xl mx-auto text-center text-xs sm:text-sm text-gray-600">
          {isLoadingApproval && <span>{t.loading}</span>}
          {submitError && <span className="text-red-600">{submitError}</span>}
          {submitMessage && <span className="text-green-600">{submitMessage}</span>}
          {!isLoadingApproval && approvalStatus && !submitMessage && !submitError && (
            <span>
              {t.status}: <strong className="text-gray-800">{approvalStatus.status}</strong>
            </span>
          )}
        </div>
      </div>

      {/* Info Banner - Hidden on very small screens */}
      <div className="bg-blue-50 border-t border-blue-100 p-2 sm:p-3 hidden xs:block">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs sm:text-sm text-blue-800 text-center">
            💡 <strong>{lang === 'fr' ? 'Conseil :' : 'Tip:'}</strong> {t.tip}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientApproval;
